import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { 
  getMembershipPlans, 
  createMembershipPlan, 
  updateMembershipPlan, 
  deleteMembershipPlan,
  getMembershipStats,
  getMembers,
  getMemberSubscriptions,
  createMemberSubscriptionCheckout,
  cancelMemberSubscription,
  trackFeatureUsage 
} from '../services/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorUtils';
import { 
  CreditCard, 
  Plus, 
  Edit2, 
  Trash2, 
  Users, 
  DollarSign, 
  Check,
  X,
  UserPlus,
  Calendar,
  TrendingUp,
  ChevronRight,
  MoreVertical,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { cn } from '../lib/utils';

const MembershipPlansPage = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Dialog states
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [subscribeDialogOpen, setSubscribeDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cancelSubDialogOpen, setCancelSubDialogOpen] = useState(false);
  
  const [editingPlan, setEditingPlan] = useState(null);
  const [deletingPlanId, setDeletingPlanId] = useState(null);
  const [cancelingSubId, setCancelingSubId] = useState(null);
  const [selectedPlanForSubscribe, setSelectedPlanForSubscribe] = useState(null);
  
  const [saving, setSaving] = useState(false);
  
  const [planForm, setPlanForm] = useState({
    name: '',
    description: '',
    price: '',
    billing_interval: 'month',
    features: ''
  });
  
  const [subscribeForm, setSubscribeForm] = useState({
    member_id: ''
  });

  const loadData = useCallback(async () => {
    try {
      const [plansRes, subsRes, membersRes, statsRes] = await Promise.all([
        getMembershipPlans(),
        getMemberSubscriptions(),
        getMembers(),
        getMembershipStats()
      ]);
      setPlans(plansRes.data);
      setSubscriptions(subsRes.data);
      setMembers(membersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load membership data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    trackFeatureUsage('membership_plans', 'view').catch(() => {});
  }, [loadData]);

  const handleOpenPlanDialog = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setPlanForm({
        name: plan.name,
        description: plan.description || '',
        price: plan.price.toString(),
        billing_interval: plan.billing_interval,
        features: plan.features?.join('\n') || ''
      });
    } else {
      setEditingPlan(null);
      setPlanForm({
        name: '',
        description: '',
        price: '',
        billing_interval: 'month',
        features: ''
      });
    }
    setPlanDialogOpen(true);
  };

  const handleSavePlan = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const planData = {
        name: planForm.name,
        description: planForm.description || null,
        price: parseFloat(planForm.price),
        billing_interval: planForm.billing_interval,
        features: planForm.features ? planForm.features.split('\n').filter(f => f.trim()) : []
      };
      
      if (editingPlan) {
        await updateMembershipPlan(editingPlan.id, planData);
        toast.success('Plan updated successfully');
      } else {
        await createMembershipPlan(planData);
        toast.success('Plan created successfully');
      }
      
      setPlanDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save plan'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlan = async () => {
    if (!deletingPlanId) return;
    
    try {
      await deleteMembershipPlan(deletingPlanId);
      toast.success('Plan deleted');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete plan'));
    } finally {
      setDeleteDialogOpen(false);
      setDeletingPlanId(null);
    }
  };

  const handleOpenSubscribeDialog = (plan) => {
    setSelectedPlanForSubscribe(plan);
    setSubscribeForm({ member_id: '' });
    setSubscribeDialogOpen(true);
  };

  const handleCreateSubscription = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const response = await createMemberSubscriptionCheckout({
        member_id: subscribeForm.member_id,
        plan_id: selectedPlanForSubscribe.id,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create subscription'));
      setSaving(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!cancelingSubId) return;
    
    try {
      await cancelMemberSubscription(cancelingSubId);
      toast.success('Subscription canceled');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to cancel subscription'));
    } finally {
      setCancelSubDialogOpen(false);
      setCancelingSubId(null);
    }
  };

  // Get members who don't have active subscriptions
  const availableMembers = members.filter(member => {
    const hasActiveSub = subscriptions.some(
      sub => sub.member_id === member.id && ['active', 'trialing'].includes(sub.status)
    );
    return !hasActiveSub && member.email;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="membership-plans-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
              Membership Plans
            </h1>
            <p className="text-zinc-400 mt-1">
              Create subscription plans and manage member payments
            </p>
          </div>
          {isAdmin && (
            <Button
              onClick={() => handleOpenPlanDialog()}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="create-plan-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Plan
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-600/20 rounded-sm">
                  <DollarSign className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Monthly Revenue</p>
                  <p className="text-2xl font-bold text-white">
                    ${stats.monthly_recurring_revenue?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-600/20 rounded-sm">
                  <Users className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Active Subscriptions</p>
                  <p className="text-2xl font-bold text-white">
                    {stats.active_subscriptions || 0}
                  </p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-600/20 rounded-sm">
                  <CreditCard className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Active Plans</p>
                  <p className="text-2xl font-bold text-white">
                    {stats.total_plans || 0}
                  </p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-600/20 rounded-sm">
                  <TrendingUp className="w-6 h-6 text-yellow-500" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Canceled</p>
                  <p className="text-2xl font-bold text-white">
                    {stats.canceled_subscriptions || 0}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="plans" className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800 p-1">
            <TabsTrigger 
              value="plans"
              className="data-[state=active]:bg-zinc-800 data-[state=active]:text-white"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Plans
            </TabsTrigger>
            <TabsTrigger 
              value="subscriptions"
              className="data-[state=active]:bg-zinc-800 data-[state=active]:text-white"
            >
              <Users className="w-4 h-4 mr-2" />
              Subscriptions
            </TabsTrigger>
          </TabsList>

          {/* Plans Tab */}
          <TabsContent value="plans">
            {plans.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {plans.map((plan, index) => (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={cn(
                      "bg-zinc-900/50 border rounded-sm overflow-hidden",
                      plan.active ? "border-zinc-800" : "border-zinc-800/50 opacity-60"
                    )}
                    data-testid={`plan-card-${plan.id}`}
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-heading text-xl font-bold text-white uppercase">
                            {plan.name}
                          </h3>
                          {!plan.active && (
                            <span className="text-xs text-zinc-500 uppercase">Inactive</span>
                          )}
                        </div>
                        {isAdmin && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-zinc-400">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                              <DropdownMenuItem 
                                onClick={() => handleOpenPlanDialog(plan)}
                                className="text-zinc-300 focus:bg-zinc-800"
                              >
                                <Edit2 className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => {
                                  setDeletingPlanId(plan.id);
                                  setDeleteDialogOpen(true);
                                }}
                                className="text-red-500 focus:bg-zinc-800"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                      
                      <div className="mb-4">
                        <span className="text-4xl font-bold text-white">${plan.price}</span>
                        <span className="text-zinc-400">/{plan.billing_interval}</span>
                      </div>
                      
                      {plan.description && (
                        <p className="text-zinc-400 text-sm mb-4">{plan.description}</p>
                      )}
                      
                      {plan.features && plan.features.length > 0 && (
                        <ul className="space-y-2 mb-6">
                          {plan.features.map((feature, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-zinc-300">
                              <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      )}
                      
                      <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                        <span className="text-sm text-zinc-400">
                          {plan.subscriber_count || 0} subscribers
                        </span>
                        {isAdmin && plan.active && (
                          <Button
                            size="sm"
                            onClick={() => handleOpenSubscribeDialog(plan)}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid={`subscribe-member-${plan.id}`}
                          >
                            <UserPlus className="w-4 h-4 mr-2" />
                            Subscribe Member
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-zinc-900/30 border border-zinc-800 rounded-sm">
                <CreditCard className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                <h3 className="text-lg font-medium text-white mb-2">No Membership Plans</h3>
                <p className="text-zinc-400 mb-6">
                  Create your first membership plan to start accepting recurring payments.
                </p>
                {isAdmin && (
                  <Button
                    onClick={() => handleOpenPlanDialog()}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Plan
                  </Button>
                )}
              </div>
            )}
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            {subscriptions.length > 0 ? (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-800">
                        <th className="text-left text-xs text-zinc-400 uppercase tracking-wider p-4">Member</th>
                        <th className="text-left text-xs text-zinc-400 uppercase tracking-wider p-4">Plan</th>
                        <th className="text-left text-xs text-zinc-400 uppercase tracking-wider p-4">Price</th>
                        <th className="text-left text-xs text-zinc-400 uppercase tracking-wider p-4">Status</th>
                        <th className="text-left text-xs text-zinc-400 uppercase tracking-wider p-4">Period End</th>
                        <th className="text-right text-xs text-zinc-400 uppercase tracking-wider p-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                      {subscriptions.map((sub) => (
                        <tr key={sub.id} className="hover:bg-zinc-800/30" data-testid={`subscription-row-${sub.id}`}>
                          <td className="p-4">
                            <div>
                              <p className="text-white font-medium">{sub.member_name}</p>
                              <p className="text-zinc-500 text-sm">{sub.member_email}</p>
                            </div>
                          </td>
                          <td className="p-4 text-zinc-300">{sub.plan_name}</td>
                          <td className="p-4 text-zinc-300">${sub.plan_price}/mo</td>
                          <td className="p-4">
                            <span className={cn(
                              "px-2 py-1 rounded-sm text-xs uppercase font-medium",
                              sub.status === 'active' && "bg-green-600/20 text-green-500",
                              sub.status === 'canceled' && "bg-red-600/20 text-red-500",
                              sub.status === 'pending' && "bg-yellow-600/20 text-yellow-500",
                              sub.status === 'past_due' && "bg-orange-600/20 text-orange-500"
                            )}>
                              {sub.status}
                            </span>
                          </td>
                          <td className="p-4 text-zinc-400">
                            {sub.current_period_end 
                              ? new Date(sub.current_period_end).toLocaleDateString('en-US')
                              : '-'
                            }
                          </td>
                          <td className="p-4 text-right">
                            {sub.status === 'active' && isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setCancelingSubId(sub.id);
                                  setCancelSubDialogOpen(true);
                                }}
                                className="text-red-500 hover:text-red-400"
                              >
                                <X className="w-4 h-4 mr-1" />
                                Cancel
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-16 bg-zinc-900/30 border border-zinc-800 rounded-sm">
                <Users className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                <h3 className="text-lg font-medium text-white mb-2">No Active Subscriptions</h3>
                <p className="text-zinc-400">
                  Subscribe members to plans to see them here.
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Create/Edit Plan Dialog */}
        <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white font-heading uppercase">
                {editingPlan ? 'Edit Plan' : 'Create Membership Plan'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSavePlan} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Plan Name</Label>
                <Input
                  value={planForm.name}
                  onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                  placeholder="e.g., Basic Membership"
                  className="bg-zinc-950 border-zinc-800 focus:border-red-600"
                  required
                  data-testid="plan-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Description</Label>
                <Textarea
                  value={planForm.description}
                  onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                  placeholder="Describe what's included..."
                  className="bg-zinc-950 border-zinc-800 focus:border-red-600 min-h-[80px]"
                  data-testid="plan-description-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Price ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={planForm.price}
                    onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })}
                    placeholder="99.00"
                    className="bg-zinc-950 border-zinc-800 focus:border-red-600"
                    required
                    data-testid="plan-price-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">Billing Interval</Label>
                  <Select 
                    value={planForm.billing_interval} 
                    onValueChange={(value) => setPlanForm({ ...planForm, billing_interval: value })}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="month">Monthly</SelectItem>
                      <SelectItem value="year">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Features (one per line)</Label>
                <Textarea
                  value={planForm.features}
                  onChange={(e) => setPlanForm({ ...planForm, features: e.target.value })}
                  placeholder="Unlimited classes&#10;Locker room access&#10;Free gear rental"
                  className="bg-zinc-950 border-zinc-800 focus:border-red-600 min-h-[100px]"
                  data-testid="plan-features-input"
                />
              </div>
              
              <DialogFooter className="pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPlanDialogOpen(false)}
                  className="border-zinc-700"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="save-plan-btn"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    editingPlan ? 'Update Plan' : 'Create Plan'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Subscribe Member Dialog */}
        <Dialog open={subscribeDialogOpen} onOpenChange={setSubscribeDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white font-heading uppercase">
                Subscribe Member to {selectedPlanForSubscribe?.name}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateSubscription} className="space-y-4 py-4">
              <div className="p-4 bg-zinc-950 border border-zinc-800 rounded-sm mb-4">
                <p className="text-zinc-400 text-sm">Plan Details</p>
                <p className="text-white text-lg font-bold">
                  ${selectedPlanForSubscribe?.price}/{selectedPlanForSubscribe?.billing_interval}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Select Member</Label>
                {availableMembers.length > 0 ? (
                  <Select 
                    value={subscribeForm.member_id} 
                    onValueChange={(value) => setSubscribeForm({ ...subscribeForm, member_id: value })}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="member-select">
                      <SelectValue placeholder="Choose a member..." />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {availableMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name} ({member.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-zinc-500 text-sm">
                    No available members. Members must have an email and not already be subscribed.
                  </p>
                )}
              </div>
              
              <p className="text-zinc-400 text-sm">
                This will redirect to Stripe Checkout where the member can enter their payment details.
              </p>
              
              <DialogFooter className="pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSubscribeDialogOpen(false)}
                  className="border-zinc-700"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving || !subscribeForm.member_id}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="create-subscription-btn"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Proceed to Checkout
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Plan Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete Plan</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Are you sure you want to delete this plan? This action cannot be undone.
                Plans with active subscriptions cannot be deleted.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeletePlan}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Cancel Subscription Confirmation */}
        <AlertDialog open={cancelSubDialogOpen} onOpenChange={setCancelSubDialogOpen}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Cancel Subscription</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Are you sure you want to cancel this subscription? 
                The member will retain access until the end of their current billing period.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700">
                Keep Subscription
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCancelSubscription}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Cancel Subscription
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default MembershipPlansPage;
